import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import PQChatPanel from '@/components/pq/PQChatPanel';
import ChecklistPanel from '@/components/checklist/ChecklistPanel';
import { PQActionLabel } from '@/components/pq/PQMark';
import {
  AlertCircle,
  RefreshCw,
  Camera,
  Upload,
  FileText,
  FileSpreadsheet,
  FileType2,
  Image as ImageIcon,
  Trash2,
  Tag as TagIcon,
  Eye,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  X as XIcon,
  Users,
  User,
  HardHat,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  fetchAllJobFiles,
  fetchJobsForFiles,
  uploadJobFile,
  deleteJobFile,
  updateJobFileMeta,
  setShareToClient,
  setShareToCrew,
  splitByKind,
  formatBytes,
  docTypeLabel,
  fileKind,
  JOB_FILE_TAGS,
} from '@/lib/jobFilesData';
import { loadAllSettings } from '@/lib/settingsData';
import {
  createSignedPortalUrls,
  isStorageUrl,
  pathFromPublicUrl,
} from '@/lib/portalStorage';

/* -------------------------------------------------------------------------
 * Doc type icon helper
 * ----------------------------------------------------------------------- */
const DocIcon = ({ mime }) => {
  if (!mime) return <FileText className="h-5 w-5 text-[hsl(var(--accent))]" />;
  if (mime.includes('pdf')) return <FileType2 className="h-5 w-5 text-rose-600 dark:text-rose-400" />;
  if (mime.includes('word') || mime.includes('msword'))
    return <FileText className="h-5 w-5 text-sky-600 dark:text-sky-400" />;
  if (mime.includes('excel') || mime.includes('spreadsheet'))
    return <FileSpreadsheet className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return <FileText className="h-5 w-5 text-amber-600 dark:text-amber-400" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
};

/* -------------------------------------------------------------------------
 * Tag pill
 * ----------------------------------------------------------------------- */
const TAG_COLOR = {
  Before: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  During: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30',
  After: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  Inspection: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
  Permit: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30',
  Material: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
};

const TagPill = ({ tag }) => {
  if (!tag) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-medium border rounded',
        TAG_COLOR[tag] || 'bg-muted text-muted-foreground border-border'
      )}
      data-testid={`job-file-tag-${tag.toLowerCase()}`}
    >
      {tag}
    </span>
  );
};

/* -------------------------------------------------------------------------
 * Upload bar
 * ----------------------------------------------------------------------- */
const UploadBar = ({ disabled, onPick, onCamera, busy }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
        onChange={(e) => {
          if (e.target.files?.length) onPick(Array.from(e.target.files));
          if (fileRef.current) fileRef.current.value = '';
        }}
        data-testid="job-files-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onCamera(Array.from(e.target.files));
          if (cameraRef.current) cameraRef.current.value = '';
        }}
        data-testid="job-files-camera-input"
      />
      <Button
        onClick={() => cameraRef.current?.click()}
        disabled={disabled || busy}
        className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
        data-testid="job-files-camera-button"
      >
        <Camera className="h-4 w-4 mr-2" />
        {t('jobFiles.takePhoto')}
      </Button>
      <Button
        onClick={() => fileRef.current?.click()}
        disabled={disabled || busy}
        variant="outline"
        className="border-border"
        data-testid="job-files-upload-button"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Upload className="h-4 w-4 mr-2" />
        )}
        {t('jobFiles.uploadFiles')}
      </Button>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Per-file share controls (rendered inside grid card / doc row)
 * ----------------------------------------------------------------------- */
const ShareControls = ({ file, hasClient, onToggleClient, onToggleCrew, busy }) => {
  const { t } = useTranslation();
  const sharedClient = !!file?.shared_with?.client;
  const crewIds = file?.shared_with?.crew_ids || [];
  const sharedCrew = crewIds.length > 0;
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      <button
        type="button"
        onClick={() => !busy && onToggleClient(!sharedClient)}
        disabled={busy || !hasClient}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[hsl(var(--background))]',
          sharedClient
            ? 'bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/40'
            : 'bg-[hsl(var(--muted))] text-muted-foreground border-border hover:text-foreground',
          (busy || !hasClient) && 'opacity-50 cursor-not-allowed'
        )}
        title={hasClient ? t('jobFiles.toggleClientShare') : t('jobFiles.noClientForToggle')}
        data-testid={`job-file-share-client-${file.id}`}
      >
        <User className="h-3.5 w-3.5" />
        <span>{t('jobFiles.client')}</span>
        {sharedClient ? <CheckCircle2 className="h-3 w-3" /> : null}
      </button>
      <button
        type="button"
        onClick={() => !busy && onToggleCrew(!sharedCrew)}
        disabled={busy}
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] focus-visible:ring-offset-1 focus-visible:ring-offset-[hsl(var(--background))]',
          sharedCrew
            ? 'bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border-[hsl(var(--accent))]/40'
            : 'bg-[hsl(var(--muted))] text-muted-foreground border-border hover:text-foreground',
          busy && 'opacity-50 cursor-not-allowed'
        )}
        data-testid={`job-file-share-crew-${file.id}`}
      >
        <HardHat className="h-3.5 w-3.5" />
        <span>{t('jobFiles.crew')}</span>
        {sharedCrew ? <CheckCircle2 className="h-3 w-3" /> : null}
      </button>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Photo card
 * ----------------------------------------------------------------------- */
const PhotoCard = ({
  file,
  jobClientId,
  onOpen,
  onDelete,
  onToggleClient,
  onToggleCrew,
  onChangeTag,
  busyId,
}) => {
  const { t } = useTranslation();
  const isBusy = busyId === file.id;
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] overflow-hidden group"
      data-testid={`job-file-photo-${file.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen(file)}
        className="block w-full aspect-square bg-[hsl(var(--muted))] overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] cursor-zoom-in"
        data-testid={`job-file-photo-open-${file.id}`}
      >
        <img
          src={file.url}
          alt={file.caption || file.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
        />
      </button>
      <CardContent className="p-3 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <TagPill tag={file.tag} />
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
              {file.shared_with?.client || file.shared_with?.crew_ids?.length
                ? t('jobFiles.shared')
                : t('jobFiles.internal')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onDelete(file)}
            disabled={isBusy}
            className="text-muted-foreground hover:text-rose-600 dark:text-rose-400 transition-colors p-1 rounded"
            title={t('jobFiles.deleteAriaLabel')}
            data-testid={`job-file-delete-${file.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <Select
          value={file.tag || ''}
          onValueChange={(v) => onChangeTag(file, v === '__none__' ? '' : v)}
        >
          <SelectTrigger
            className="h-8 text-xs bg-[hsl(var(--muted))] border-border"
            data-testid={`job-file-tag-select-${file.id}`}
          >
            <SelectValue placeholder={t('jobFiles.addTag')} />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border">
            <SelectItem value="__none__">{t('jobFiles.tagsNone')}</SelectItem>
            {JOB_FILE_TAGS.map((tag) => (
              <SelectItem key={tag} value={tag}>{tag}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ShareControls
          file={file}
          hasClient={!!jobClientId}
          onToggleClient={(v) => onToggleClient(file, v)}
          onToggleCrew={(v) => onToggleCrew(file, v)}
          busy={isBusy}
        />
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Document row
 * ----------------------------------------------------------------------- */
const DocRow = ({
  file,
  jobClientId,
  onDelete,
  onToggleClient,
  onToggleCrew,
  onChangeTag,
  busyId,
}) => {
  const { t } = useTranslation();
  const isBusy = busyId === file.id;
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={`job-file-doc-${file.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
            <DocIcon mime={file.mime} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-foreground font-medium hover:text-[hsl(var(--accent))] transition-colors truncate"
                title={file.name}
                data-testid={`job-file-doc-open-${file.id}`}
              >
                {file.name || t('common.document', { defaultValue: 'Document' })}
              </a>
              <TagPill tag={file.tag} />
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {docTypeLabel(file.mime)} · {formatBytes(file.size)} ·{' '}
              {new Date(file.uploaded_at).toLocaleDateString()}
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <Select
                value={file.tag || ''}
                onValueChange={(v) => onChangeTag(file, v === '__none__' ? '' : v)}
              >
                <SelectTrigger
                  className="h-8 text-xs bg-[hsl(var(--muted))] border-border w-32"
                  data-testid={`job-file-tag-select-${file.id}`}
                >
                  <SelectValue placeholder={t('jobFiles.tagsLabel')} />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="__none__">{t('jobFiles.tagsNone')}</SelectItem>
                  {JOB_FILE_TAGS.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobFiles.docsNotPushed')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors p-2 rounded"
              title={t('jobFiles.openAriaLabel')}
              data-testid={`job-file-doc-link-${file.id}`}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => onDelete(file)}
              disabled={isBusy}
              className="text-muted-foreground hover:text-rose-600 dark:text-rose-400 transition-colors p-2 rounded"
              title={t('jobFiles.deleteAriaLabel')}
              data-testid={`job-file-doc-delete-${file.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Fullscreen photo viewer
 * ----------------------------------------------------------------------- */
const PhotoViewer = ({ open, photos, index, onClose, onPrev, onNext }) => {
  const { t } = useTranslation();
  const photo = photos[index];
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onPrev, onNext]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const handleTouchEnd = (e) => {
    const start = touchStartX.current;
    const end = e.changedTouches?.[0]?.clientX ?? null;
    touchStartX.current = null;
    if (start === null || end === null) return;
    const dx = end - start;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) onPrev();
    else onNext();
  };

  if (!photo) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-[100vw] sm:max-w-[95vw] h-[100dvh] sm:h-[90vh] p-0 bg-[hsl(var(--background))] border-0 overflow-hidden"
        data-testid="job-file-viewer"
      >
        <div
          className="relative w-full h-full flex flex-col"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-[hsl(var(--hairline))]">
            <div className="flex items-center gap-2 min-w-0">
              <TagPill tag={photo.tag} />
              <span className="text-xs sm:text-sm text-muted-foreground truncate">
                {photo.caption || photo.name}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobFiles.viewerCounter', { current: index + 1, total: photos.length })}
              </span>
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded hover:bg-[hsl(var(--muted))] transition-colors"
                title={t('actions.close', { defaultValue: 'Close' })}
                data-testid="job-file-viewer-close"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          {/* Image area */}
          <div className="flex-1 min-h-0 relative bg-[hsl(var(--muted))]/40 flex items-center justify-center">
            <img
              src={photo.url}
              alt={photo.caption || photo.name}
              className="max-h-full max-w-full object-contain select-none"
              draggable={false}
            />
            {photos.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={onPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-[hsl(var(--background))]/70 backdrop-blur border border-[hsl(var(--hairline))] hover:bg-[hsl(var(--background))]"
                  title={t('schedule.prevAria')}
                  data-testid="job-file-viewer-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full bg-[hsl(var(--background))]/70 backdrop-blur border border-[hsl(var(--hairline))] hover:bg-[hsl(var(--background))]"
                  title={t('schedule.nextAria')}
                  data-testid="job-file-viewer-next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------
 * Main page
 * ----------------------------------------------------------------------- */
export default function JobFilesPage() {
  const { t } = useTranslation();
  // PQ carries money tools (job financials, spend) — the chat entry point is
  // admin-only even though this page is visible to crew leads.
  const { isAdmin } = useAuth();
  const [pqChatOpen, setPqChatOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [allFiles, setAllFiles] = useState({});
  const [crewRoster, setCrewRoster] = useState([]);
  const [activeJobId, setActiveJobId] = useState('');
  const [tagFilter, setTagFilter] = useState('all');
  const [pendingTag, setPendingTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIdx, setViewerIdx] = useState(0);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const [jobsList, files, settings] = await Promise.all([
        fetchJobsForFiles(),
        fetchAllJobFiles(),
        loadAllSettings(),
      ]);
      setJobs(jobsList);
      setAllFiles(files);
      setCrewRoster(settings.crew || []);
      if (!activeJobId && jobsList.length) {
        setActiveJobId(jobsList[0].id);
      }
    } catch (e) {
      setError(e?.message || t('jobFiles.loadError'));
    } finally {
      setLoading(false);
    }
  }, [activeJobId, t]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeJob = useMemo(
    () => jobs.find((j) => j.id === activeJobId) || null,
    [jobs, activeJobId]
  );

  const filesForJob = useMemo(
    () => allFiles[activeJobId] || [],
    [allFiles, activeJobId]
  );

  const filteredFiles = useMemo(() => {
    if (tagFilter === 'all') return filesForJob;
    if (tagFilter === '__notag__') return filesForJob.filter((f) => !f.tag);
    return filesForJob.filter((f) => f.tag === tagFilter);
  }, [filesForJob, tagFilter]);

  /* ------------------- Signed URLs for the private bucket -------------------
   * portal-photos is private (sql/021), so stored public URLs no longer
   * resolve. Sign fresh display URLs per file; keyed by file.id and applied
   * only at render so expiring signed links never reach persisted settings
   * JSON (allFiles stays pristine for updateJobFileMeta/share/delete). */
  const [signedUrlById, setSignedUrlById] = useState({});

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const targets = filesForJob
        .map((f) => ({
          id: f.id,
          path:
            f.path || (isStorageUrl(f.url) ? pathFromPublicUrl(f.url) : null),
        }))
        .filter((t) => t.path);
      if (targets.length === 0) {
        setSignedUrlById({});
        return;
      }
      const byPath = await createSignedPortalUrls(targets.map((t) => t.path));
      if (cancelled) return;
      const next = {};
      targets.forEach((t) => {
        if (byPath[t.path]) next[t.id] = byPath[t.path];
      });
      setSignedUrlById(next);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [filesForJob]);

  const displayFiles = useMemo(
    () =>
      filteredFiles.map((f) =>
        signedUrlById[f.id] ? { ...f, url: signedUrlById[f.id] } : f
      ),
    [filteredFiles, signedUrlById]
  );

  const { photos, docs } = useMemo(() => splitByKind(displayFiles), [displayFiles]);
  const totalPhotos = useMemo(() => splitByKind(filesForJob).photos.length, [filesForJob]);
  const totalDocs = useMemo(() => splitByKind(filesForJob).docs.length, [filesForJob]);

  /* ------------------------- Upload ------------------------- */
  const doUpload = async (filesArr) => {
    if (!activeJobId) {
      toast.error(t('jobFiles.pickJobFirst'));
      return;
    }
    if (!filesArr || filesArr.length === 0) return;
    setBusy(true);
    let okCount = 0;
    let failCount = 0;
    let workingMap = allFiles;
    try {
      for (const f of filesArr) {
        try {
          const { next } = await uploadJobFile(
            { jobId: activeJobId, file: f, tag: pendingTag || '' },
            workingMap
          );
          workingMap = next;
          okCount += 1;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[job-files] upload failed', err);
          failCount += 1;
        }
      }
      setAllFiles(workingMap);
      if (okCount > 0) {
        if (failCount > 0) {
          toast.success(
            t('jobFiles.uploadedWithFails', { count: okCount, fails: failCount })
          );
        } else {
          toast.success(t('jobFiles.uploaded', { count: okCount }));
        }
      } else {
        toast.error(t('jobFiles.uploadsAllFailed', { count: failCount }));
      }
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------- Tag change ------------------------- */
  const doChangeTag = async (file, tag) => {
    setBusyId(file.id);
    try {
      const next = await updateJobFileMeta(
        { jobId: activeJobId, fileId: file.id, tag },
        allFiles
      );
      setAllFiles(next);
    } catch (e) {
      toast.error(e?.message || t('jobFiles.tagUpdateFailed'));
    } finally {
      setBusyId(null);
    }
  };

  /* ------------------------- Share toggles ------------------------- */
  const allCrewIds = useMemo(() => crewRoster.map((c) => c.id).filter(Boolean), [crewRoster]);

  const doToggleClient = async (file, enabled) => {
    setBusyId(file.id);
    try {
      const clientId = activeJob?.client_id;
      if (enabled && !clientId) {
        toast.error(t('jobFiles.noClientHint'));
        return;
      }
      const next = await setShareToClient(
        { jobId: activeJobId, fileId: file.id, enabled, clientId },
        allFiles
      );
      setAllFiles(next);
      toast.success(enabled ? t('jobFiles.sharedWithClient') : t('jobFiles.removedFromClient'));
    } catch (e) {
      toast.error(e?.message || t('jobFiles.shareUpdateFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const doToggleCrew = async (file, enabled) => {
    setBusyId(file.id);
    try {
      const next = await setShareToCrew(
        {
          jobId: activeJobId,
          fileId: file.id,
          crewIds: enabled ? allCrewIds : [],
          jobName: activeJob?.name || '',
        },
        allFiles
      );
      setAllFiles(next);
      toast.success(enabled ? t('jobFiles.sharedWithCrew') : t('jobFiles.removedFromCrew'));
    } catch (e) {
      toast.error(e?.message || t('jobFiles.shareUpdateFailed'));
    } finally {
      setBusyId(null);
    }
  };

  /* ------------------------- Delete ------------------------- */
  const doDelete = async () => {
    if (!confirmDelete) return;
    const file = confirmDelete;
    setBusyId(file.id);
    try {
      const next = await deleteJobFile(
        { jobId: activeJobId, fileId: file.id },
        allFiles
      );
      setAllFiles(next);
      toast.success(t('jobFiles.fileDeleted'));
    } catch (e) {
      toast.error(e?.message || t('jobFiles.deleteFailed'));
    } finally {
      setBusyId(null);
      setConfirmDelete(null);
    }
  };

  /* ------------------------- Viewer nav ------------------------- */
  const openViewer = (file) => {
    const idx = photos.findIndex((p) => p.id === file.id);
    setViewerIdx(idx >= 0 ? idx : 0);
    setViewerOpen(true);
  };
  const viewerPrev = () =>
    setViewerIdx((i) => (photos.length ? (i - 1 + photos.length) % photos.length : 0));
  const viewerNext = () =>
    setViewerIdx((i) => (photos.length ? (i + 1) % photos.length : 0));

  /* ------------------------- Render ------------------------- */
  if (loading) {
    return (
      <div data-testid="job-files-page-loading" className="space-y-4">
        <PageHeader eyebrow={t('jobFiles.eyebrow')} title={t('jobFiles.title')} subtitle={t('jobFiles.subtitle')} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 bg-[hsl(var(--muted))]" />
          ))}
        </div>
        <Skeleton className="h-64 bg-[hsl(var(--muted))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="job-files-page-error" className="space-y-4">
        <PageHeader eyebrow={t('jobFiles.eyebrow')} title={t('jobFiles.title')} />
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-foreground">{error}</div>
              <Button
                onClick={reload}
                variant="outline"
                className="mt-3 border-border"
                data-testid="job-files-retry"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('jobFiles.retry')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="job-files-page" className="space-y-5">
      <PageHeader
        eyebrow={t('jobFiles.eyebrow')}
        title={t('jobFiles.title')}
        subtitle={t('jobFiles.subtitle')}
        actions={
          <Button
            onClick={reload}
            variant="outline"
            className="border-border"
            data-testid="job-files-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('jobFiles.refresh')}
          </Button>
        }
      />

      {jobs.length === 0 ? (
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-8 text-center">
            <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <div className="text-sm text-foreground mb-1">{t('jobFiles.noJobsTitle')}</div>
            <div className="text-xs text-muted-foreground">
              {t('jobFiles.noJobsHint')}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Job picker + filter row */}
          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr_1fr] gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    {t('jobFiles.job')}
                  </div>
                  <Select value={activeJobId} onValueChange={setActiveJobId}>
                    <SelectTrigger
                      className="h-10 bg-[hsl(var(--muted))] border-border"
                      data-testid="job-files-job-picker"
                    >
                      <SelectValue placeholder={t('jobFiles.pickJob')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                      {jobs.map((j) => (
                        <SelectItem key={j.id} value={j.id}>
                          {j.name} {j.client ? `\u2014 ${j.client}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    {t('jobFiles.defaultTag')}
                  </div>
                  <Select value={pendingTag || '__none__'} onValueChange={(v) => setPendingTag(v === '__none__' ? '' : v)}>
                    <SelectTrigger
                      className="h-10 bg-[hsl(var(--muted))] border-border"
                      data-testid="job-files-pending-tag"
                    >
                      <SelectValue placeholder={t('jobFiles.untagged')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-border">
                      <SelectItem value="__none__">{t('jobFiles.tagsNone')}</SelectItem>
                      {JOB_FILE_TAGS.map((tag) => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
                    {t('jobFiles.filter')}
                  </div>
                  <Select value={tagFilter} onValueChange={setTagFilter}>
                    <SelectTrigger
                      className="h-10 bg-[hsl(var(--muted))] border-border"
                      data-testid="job-files-tag-filter"
                    >
                      <SelectValue placeholder={t('jobFiles.all')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-border">
                      <SelectItem value="all">{t('jobFiles.allFiles')}</SelectItem>
                      <SelectItem value="__notag__">{t('jobFiles.untagged')}</SelectItem>
                      {JOB_FILE_TAGS.map((tag) => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Separator className="bg-border" />
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <UploadBar
                  disabled={!activeJobId}
                  busy={busy}
                  onPick={doUpload}
                  onCamera={doUpload}
                />
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {isAdmin ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPqChatOpen(true)}
                      disabled={!activeJob?.id}
                      className="group h-10 px-1.5 hover:bg-transparent focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]"
                      data-testid="job-files-ask-pq-button"
                    >
                      <PQActionLabel
                        monogram="after"
                        label={t('pqchat.ask_label', { defaultValue: 'Ask' })}
                      />
                    </Button>
                  ) : null}
                  <span data-testid="job-files-count-photos">
                    <ImageIcon className="h-3.5 w-3.5 inline mr-1" />
                    {t('jobFiles.photoCount', { count: totalPhotos })}
                  </span>
                  <span data-testid="job-files-count-docs">
                    <FileText className="h-3.5 w-3.5 inline mr-1" />
                    {t('jobFiles.docCount', { count: totalDocs })}
                  </span>
                  {activeJob?.client_id ? (
                    <span title={t('jobFiles.clientLinkedTitle')} className="inline-flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" /> {activeJob.client || t('jobFiles.client')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600/80 dark:text-amber-400/80">
                      <AlertCircle className="h-3.5 w-3.5" /> {t('jobFiles.noClientLinked')}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {isAdmin && activeJob?.id ? (
            <ChecklistPanel jobId={activeJob.id} refreshKey={pqChatOpen ? 0 : 1} />
          ) : null}

          {/* Photos / Docs */}
          <Tabs defaultValue="photos">
            <TabsList className="bg-[hsl(var(--card))] border border-border">
              <TabsTrigger value="photos" data-testid="job-files-tab-photos">
                <ImageIcon className="h-4 w-4 mr-2" />
                {t('jobFiles.photosTab')}
              </TabsTrigger>
              <TabsTrigger value="docs" data-testid="job-files-tab-docs">
                <FileText className="h-4 w-4 mr-2" />
                {t('jobFiles.docsTab')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="photos" className="mt-4">
              {photos.length === 0 ? (
                <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
                  <CardContent className="p-10 text-center">
                    <ImageIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <div className="text-sm text-foreground mb-1">{t('jobFiles.noPhotos')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('jobFiles.noPhotosHint')}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
                  data-testid="job-files-photos-grid"
                >
                  {photos.map((f) => (
                    <PhotoCard
                      key={f.id}
                      file={f}
                      jobClientId={activeJob?.client_id}
                      onOpen={openViewer}
                      onDelete={(file) => setConfirmDelete(file)}
                      onToggleClient={doToggleClient}
                      onToggleCrew={doToggleCrew}
                      onChangeTag={doChangeTag}
                      busyId={busyId}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="docs" className="mt-4">
              {docs.length === 0 ? (
                <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
                  <CardContent className="p-10 text-center">
                    <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <div className="text-sm text-foreground mb-1">{t('jobFiles.noDocs')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('jobFiles.noDocsHint')}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2.5" data-testid="job-files-docs-list">
                  {docs.map((f) => (
                    <DocRow
                      key={f.id}
                      file={f}
                      jobClientId={activeJob?.client_id}
                      onDelete={(file) => setConfirmDelete(file)}
                      onToggleClient={doToggleClient}
                      onToggleCrew={doToggleCrew}
                      onChangeTag={doChangeTag}
                      busyId={busyId}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Photo viewer */}
      <PhotoViewer
        open={viewerOpen}
        photos={photos}
        index={viewerIdx}
        onClose={() => setViewerOpen(false)}
        onPrev={viewerPrev}
        onNext={viewerNext}
      />

      {/* Delete confirmation */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}
      >
        <AlertDialogContent
          className="bg-[hsl(var(--card))] border-border"
          data-testid="job-file-delete-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('jobFiles.confirmDelete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('jobFiles.confirmDelete.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="job-file-delete-cancel">{t('jobFiles.confirmDelete.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-500 text-white hover:bg-rose-500/90"
              data-testid="job-file-delete-confirm-button"
            >
              {t('jobFiles.confirmDelete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PQ on every job — one PQ, a thread per job (admin-only entry). No
          onApplyProposal here: there's no open builder, so any drafted cards
          render with a "open the estimate to apply" hint instead of Apply. */}
      {isAdmin ? (
        <PQChatPanel
          open={pqChatOpen}
          onOpenChange={setPqChatOpen}
          context={{
            type: 'job',
            id: activeJob?.id || null,
            title: activeJob?.name || '',
          }}
        />
      ) : null}
    </div>
  );
}
